import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { DEFAULT_MAX_DATE, DEFAULT_MIN_DATE } from "go-core/components/Formatters/FormatDate";
import { useWindowSize } from "go-core/components/useWindowSize";
import { FormDateRangePicker } from "go-form/components/FormDateRangePicker";
import isPredefinedRange from "go-list/utils/daterangeutils";
import { DashboardFilterActions } from "../../services/filters/reducer";
import useDashboardApi from "../../services/hooks/useDashboardApi";
import useDashboardContext from "../../services/hooks/useDashboardContext";

interface Props {
	selectedFilter?: string | null;
	isPredefinedDate: boolean;
	selectedDateFilter?: string | null;
	setSelectedDateFilter?: (value: string | null) => void;
}

const DashboardFilterDateRange = ({
	isPredefinedDate,
	selectedDateFilter,
	setSelectedDateFilter,
}: Props): JSX.Element => {
	const {
		state: {
			segmentsConfig: {
				selectedSegment: {
					value: {
						filtersConfig: {
							selectedFilter: { dateRange: dateRangeFromSegment },
						},
					},
				},
			},
			filtersConfig: {
				selectedFilter: { dateRange: dateRangeFromState },
			},
		},
		dispatch,
	} = useDashboardContext();
	const { isEditMode } = useDashboardContext();
	const wrapperRef = useRef<HTMLDivElement | null>(null);
	const [refId, setRefId] = useState<HTMLDivElement | null>(null);
	const { t } = useTranslation();
	const form = useForm<Props>({
		defaultValues: {
			selectedFilter: dateRangeFromSegment,
		},
	});
	const {
		control,
		formState: { errors },
		watch,
		setValue,
	} = form;
	const watchedDate = watch("selectedFilter");
	const pickerEl = document.getElementById("dropdown-daterange-selectedFilter") as HTMLInputElement;
	const isMobile = useWindowSize().isMobile;
	const dateRangeKey = useRef(0);
	const { fetchDataAndApplyNewDataToDashboardState } = useDashboardApi();

	useEffect(() => {
		if (isPredefinedRange(dateRangeFromState)) {
			setValue("selectedFilter", null);
		}
		if (!isPredefinedRange(dateRangeFromSegment)) {
			dateRangeKey.current++;
			setValue("selectedFilter", dateRangeFromSegment.replace(",", " - "));
		}
	}, [setValue, dateRangeFromSegment, dateRangeFromState]);

	useEffect(() => {
		if (pickerEl && isPredefinedDate) pickerEl.value = "";
	}, [dateRangeFromState, isPredefinedDate]);

	useEffect(() => {
		if (refId) {
			wrapperRef.current = refId;
		}
	}, [watchedDate]);

	const handleClickOutside = useCallback(
		async (event: MouseEvent) => {
			if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
				const date = watch().selectedFilter;
				if (isMobile) {
					if (date && selectedDateFilter !== date) {
						const value = date.replace(" - ", ",");
						if (setSelectedDateFilter) setSelectedDateFilter(value);
						setValue("selectedFilter", value);
						wrapperRef.current = null;
					}
					return;
				}
				if (date && dateRangeFromState !== date) {
					dispatch({
						type: DashboardFilterActions.CHANGE_DATE_FILTER,
						data: date.replace(" - ", ","),
					});

					wrapperRef.current = null;

					fetchDataAndApplyNewDataToDashboardState({
						selectedDateRange: date.replace(" - ", ","),
					});
				}
			}
		},
		[
			watch,
			setValue,
			dateRangeFromState,
			dispatch,
			isMobile,
			setSelectedDateFilter,
			selectedDateFilter,
			fetchDataAndApplyNewDataToDashboardState,
		]
	);

	useEffect(() => {
		if (wrapperRef.current) {
			setRefId(wrapperRef.current);
		}
		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [wrapperRef]);

	const renderDatePicker = useMemo(() => {
		return (
			<FormDateRangePicker
				timeStart={"00:00"}
				timeEnd={"23:59"}
				errors={errors}
				control={control}
				name={"selectedFilter"}
				minDate={DEFAULT_MIN_DATE}
				maxDate={DEFAULT_MAX_DATE}
				onApply={handleClickOutside}
				placeholder={t("modules.dashboard.field.date_range.title")}
				handleInternalChange={
					isMobile
						? (value) => {
								setValue("selectedFilter", value);
								if (setSelectedDateFilter) setSelectedDateFilter(value);
						  }
						: undefined
				}
				key={dateRangeKey.current}
				disabled={isEditMode}
			/>
		);
	}, [isMobile, selectedDateFilter, dateRangeKey.current, isEditMode, setSelectedDateFilter, handleClickOutside]);

	return (
		<div className={"filter-date-range-wrapper"} ref={wrapperRef}>
			{renderDatePicker}
		</div>
	);
};
export default DashboardFilterDateRange;
